import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, FolderOpen, MoreVertical, Trash2, Workflow } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/cn';

function formatCreatorName(name) {
  if (!name || typeof name !== 'string') return 'Unknown';
  const trimmed = name.trim();
  if (!trimmed) return 'Unknown';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Unknown';
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first} ${lastInitial}.`;
}

function SortableHeader({ columnId, label, sortField, sortDirection, onSort }) {
  const isSorted = sortField === columnId;
  return (
    <button
      type="button"
      className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground transition-colors select-none"
      onClick={() => onSort(columnId)}
    >
      {label}
      {isSorted ? (
        sortDirection === 'asc' ? (
          <ArrowUp className="w-3.5 h-3.5" />
        ) : (
          <ArrowDown className="w-3.5 h-3.5" />
        )
      ) : (
        <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />
      )}
    </button>
  );
}

export default function ProjectsDataTable({
  data,
  sortField,
  sortDirection,
  handleSort,
  showAssigneeColumn,
  canCreateTasks,
  canDeleteProjects = false,
  adminUsers = [],
  assigneeLoadingId,
  setAssigneeLoadingId,
  openDropdownId,
  setOpenDropdownId,
  dropdownRef,
  handleViewProject,
  handleDeleteProject,
  projectService,
  fetchMyProjects,
  canSetWorkflow = false,
  onSetWorkflow,
}) {
  const columns = useMemo(() => {
    const cols = [
      {
        id: 'index',
        header: '#',
        size: 60,
        cell: ({ row }) => (
          <span className="text-xs text-foreground">
            {row.original.id?.substring(0, 6)}
          </span>
        ),
      },
      {
        id: 'title',
        header: () => (
          <SortableHeader
            columnId="title"
            label="Project Name"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        ),
        size: 180,
        cell: ({ row }) => {
          const p = row.original;
          const isSynopsisProject = p.projectType === 'synopsis' || p.projectType === 'synopsis_generation' || p.projectType === 'synopsis_comparison';
          const isKeywordProject = p.projectType === 'keyword' || p.projectType === 'keyword_generation';
          const desc = p.description || (p.projectType === 'synopsis_comparison' ? 'Synopsis Comparison Project' : isSynopsisProject ? 'Synopsis Generation Project' : isKeywordProject ? 'Keyword Generation Project' : 'Transcription Project');
          return (
            <div className="min-w-0 max-w-[180px] overflow-hidden" title={[p.title, desc].filter(Boolean).join(' — ')}>
              <h4 className="text-sm font-medium text-foreground truncate" title={p.title}>{p.title}</h4>
              <p className="text-xs text-muted-foreground truncate block" title={desc}>{desc}</p>
            </div>
          );
        },
      },
      {
        id: 'projectType',
        header: 'Project Type',
        size: 120,
        cell: ({ row }) => {
          const p = row.original;
          const isKeywordProject = p.projectType === 'keyword' || p.projectType === 'keyword_generation';
          const isSynopsisProject = p.projectType === 'synopsis' || p.projectType === 'synopsis_generation' || p.projectType === 'synopsis_comparison';
          return (
            <span className={cn(
              'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
              p.projectType === 'synopsis_comparison' ? 'bg-muted text-muted-foreground' : isSynopsisProject ? 'bg-primary/15 text-primary' : isKeywordProject ? 'bg-accent/20 text-accent' : 'bg-accent/20 text-accent'
            )}>
              {p.projectType === 'synopsis_comparison' ? 'Comparison' : isSynopsisProject ? 'Generation' : isKeywordProject ? 'Keyword Generation' : 'Transcription'}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: () => (
          <SortableHeader columnId="createdAt" label="Created Date" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        ),
        size: 100,
        cell: ({ row }) => (
          <span className="text-sm text-foreground">
            {new Date(row.original.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        ),
      },
      {
        id: 'creator',
        header: 'Creator',
        size: 100,
        cell: ({ row }) => {
          const raw = row.original.creatorName || row.original.creator_name;
          const display = formatCreatorName(raw);
          const isClient = row.original.created_via === 'client';
          return (
            <span className="text-xs text-foreground truncate block" title={raw ? (isClient ? `${raw} (Client)` : raw) : undefined}>
              {display}{isClient ? ' (Client)' : ''}
            </span>
          );
        },
      },
      {
        id: 'assignee',
        header: () => (
          <SortableHeader columnId="assignee" label="Assignee" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        ),
        size: 100,
        cell: ({ row }) => {
          const name = row.original.assigneeName ?? row.original.keyword_assignee_name ?? row.original.synopsis_assignee_name ?? row.original.transcription_assignee_name;
          const display = name ? formatCreatorName(name) : '—';
          return (
            <span className="text-xs text-foreground truncate block" title={name || undefined}>
              {display}
            </span>
          );
        },
      },
      {
        id: 'workflow',
        header: () => (
          <SortableHeader columnId="workflow" label="Workflow" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        ),
        size: 120,
        cell: ({ row }) => {
          const name = row.original.workflowStatusDisplayName ?? row.original.workflow_status_display_name ?? row.original.workflowStatus ?? row.original.workflow_status;
          return (
            <span className="text-xs text-foreground truncate block" title={name || undefined}>
              {name || '—'}
            </span>
          );
        },
      },
    ];

    cols.push(
      {
        id: 'status',
        header: () => (
          <SortableHeader columnId="status" label="Status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        ),
        size: 90,
        cell: ({ row }) => {
          const p = row.original;
          const isSynopsisProject = p.projectType === 'synopsis' || p.projectType === 'synopsis_generation' || p.projectType === 'synopsis_comparison';
          const isKeywordProject = p.projectType === 'keyword' || p.projectType === 'keyword_generation';
          // Keyword (standalone) projects have no files; use project status instead of file/transcript count
          const isCompleted = isKeywordProject
            ? (p.status || 'active').toLowerCase() === 'active'
            : isSynopsisProject
              ? (p.fileCount > 0 && (p.synopsisCount || 0) > 0)
              : (p.fileCount > 0 && p.transcriptCount > 0);
          const statusColor = isCompleted ? 'green' : 'red';
          const statusLabel = isKeywordProject ? (isCompleted ? 'Active' : 'Failed') : (isCompleted ? 'Success' : 'Failed');
          return (
            <span className={cn(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              statusColor === 'green' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
            )}>
              <span className={cn('w-1.5 h-1.5 rounded-full mr-1.5', statusColor === 'green' ? 'bg-green-400' : 'bg-red-400')} />
              {statusLabel}
            </span>
          );
        },
      },
      {
        id: 'language',
        header: () => (
          <SortableHeader columnId="language" label="Source Language" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        ),
        size: 80,
        cell: ({ row }) => {
          const lang = row.original.transcriptLanguage || row.original.transcript_language || row.original.language;
          return (
            <span className="text-xs text-foreground">
              {lang && lang.toLowerCase() !== 'unknown' ? lang : '-'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        cell: ({ row }) => {
          const project = row.original;
          return (
            <div className="flex justify-end relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setOpenDropdownId(openDropdownId === project.id ? null : project.id)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-gray-500 dark:text-neutral-400" />
              </button>
              {openDropdownId === project.id && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg shadow-xl z-[1100] py-1"
                >
                  <button
                    type="button"
                    onClick={() => { handleViewProject(project); setOpenDropdownId(null); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 flex items-center"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" /> View Details
                  </button>
                  {canSetWorkflow && onSetWorkflow && (project.workflowStatus ?? project.workflow_status) !== 'delivered' && (
                    <button
                      type="button"
                      onClick={() => { onSetWorkflow(project); setOpenDropdownId(null); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 flex items-center"
                    >
                      <Workflow className="w-4 h-4 mr-2" /> Set Workflow
                    </button>
                  )}
                  {canDeleteProjects && (
                    <button
                      type="button"
                      onClick={() => { handleDeleteProject(project.id); setOpenDropdownId(null); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Project
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        },
      }
    );

    return cols.map((col) => ({
      ...(col.id === 'index' ? { accessorKey: 'id' } : col.id === 'actions' ? { accessorFn: () => '' } : { accessorKey: col.id }),
      id: col.id,
      header: col.header,
      cell: col.cell,
      size: col.size,
    }));
  }, [
    sortField, sortDirection, handleSort, canDeleteProjects,
    canSetWorkflow, onSetWorkflow,
    openDropdownId, setOpenDropdownId, dropdownRef,
    handleViewProject, handleDeleteProject,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-lg border border-gray-200 dark:border-neutral-800 overflow-visible">
      <Table>
        <TableHeader className="bg-gray-50 dark:bg-neutral-900">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.column.columnDef.size ?? undefined }} className="text-xs">
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => handleViewProject(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="text-xs py-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
